import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["entries", "pagination"]
    static values = {
        url: String,
        page: { type: Number, default: 1 },
        loading: { type: Boolean, default: false },
        reachedEnd: { type: Boolean, default: false },
        scrollContainerSelector: String
    }

    connect() {
        this.scroll = this.scroll.bind(this)
        this.scrollContainer = this.resolveScrollContainer()

        if (this.scrollContainer) {
            this.scrollContainer.addEventListener("scroll", this.scroll)
            // Initialize reachedEnd based on initial hasMore state
            this.reachedEndValue = !this.hasMorePages()
        }
    }

    resolveScrollContainer() {
        if (this.hasScrollContainerSelectorValue) {
            const closestContainer = this.element.closest(this.scrollContainerSelectorValue)
            if (closestContainer) {
                return closestContainer
            }

            const selectorMatch = document.querySelector(this.scrollContainerSelectorValue)
            if (selectorMatch) {
                return selectorMatch
            }
        }

        return this.findScrollContainer()
    }

    findScrollContainer() {
        // Start from the element
        let element = this.element

        // Check if the element itself is scrollable
        if (this.isScrollable(element)) {
            return element
        }

        // Walk up the DOM tree to find the scrollable container
        while (element && element !== document.body) {
            element = element.parentElement
            if (element && this.isScrollable(element)) {
                return element
            }
        }

        // Fallback to window if no scrollable container found
        return window
    }

    isScrollable(element) {
        if (!element || element === window || element === document) {
            return false
        }

        const style = window.getComputedStyle(element)
        const overflowY = style.overflowY
        const overflow = style.overflow

        // Check if element has overflow-y-auto or overflow-auto
        const hasOverflow = overflowY === "auto" || overflowY === "scroll" || 
                           overflow === "auto" || overflow === "scroll"

        if (!hasOverflow) {
            return false
        }

        // Check if element actually has scrollable content
        return element.scrollHeight > element.clientHeight
    }

    disconnect() {
        if (this.scrollContainer) {
            this.scrollContainer.removeEventListener("scroll", this.scroll)
        }
    }

    scroll() {
        // Don't trigger if already loading, or if we've reached the end
        if (this.loadingValue || this.reachedEndValue) return

        const scrollTop = this.scrollContainer === window 
            ? window.pageYOffset || document.documentElement.scrollTop 
            : this.scrollContainer.scrollTop
        const scrollHeight = this.scrollContainer === window 
            ? document.documentElement.scrollHeight 
            : this.scrollContainer.scrollHeight
        const clientHeight = this.scrollContainer === window 
            ? window.innerHeight 
            : this.scrollContainer.clientHeight

        const scrollPercentage = (scrollTop + clientHeight) / scrollHeight

        // Trigger load when user scrolls to 80% of the content
        if (scrollPercentage > 0.8 && this.hasMorePages()) {
            this.loadMore()
        }
    }

    hasMorePages() {
        if (!this.hasPaginationTarget) {
            return false
        }
        return this.paginationTarget.dataset.hasMore === "true"
    }

    async loadMore() {
        if (this.loadingValue || this.reachedEndValue) return

        this.loadingValue = true
        this.pageValue += 1

        try {
            const url = new URL(this.urlValue, window.location.origin)
            url.searchParams.set("page", this.pageValue)
            url.searchParams.set("format", "js")

            const response = await fetch(url, {
                headers: {
                    "Accept": "text/html",
                    "X-Requested-With": "XMLHttpRequest"
                }
            })

            if (response.ok) {
                const html = await response.text()
                const trimmedHtml = html.trim()


                // If response is empty, we've reached the end
                if (!trimmedHtml) {
                    this.reachedEndValue = true
                    return
                }

                // Store scroll position
                const scrollPos = this.scrollContainer === window 
                    ? window.pageYOffset || document.documentElement.scrollTop 
                    : this.scrollContainer.scrollTop

                // Append new content
                this.entriesTarget.insertAdjacentHTML("beforeend", trimmedHtml)

                // Restore scroll position to prevent jump
                if (this.scrollContainer === window) {
                    window.scrollTo(0, scrollPos)
                } else {
                    this.scrollContainer.scrollTop = scrollPos
                }

                // Reinitialize Preline components for newly appended content
                try {
                    if (window.HSStaticMethods && typeof window.HSStaticMethods.autoInit === "function") {
                        window.HSStaticMethods.autoInit()
                    }
                } catch (e) {
                    console.warn("Preline reinitialization failed:", e)
                }
            } else {
                // If response is not ok, mark as reached end to prevent further requests
                this.reachedEndValue = true
            }
        } catch (error) {
            console.error("Error loading more items:", error)
            // Mark as reached end on error to prevent infinite error loops
            this.reachedEndValue = true
        } finally {
            this.loadingValue = false
        }
    }

    // Method to reset the scroll state (useful when search/filter changes)
    reset() {
        this.pageValue = 1
        this.reachedEndValue = false
        this.loadingValue = false
    }
}
